import type { LegalDocumentWords } from "./legal-words.ts";

// The Privacy Policy in Spanish, a translation of legal-privacy-en.ts (ADR-024, D701, D704). The
// English text controls; change the Spanish text in the same commit as the English one. Updated 27
// October 2026 when the retention page (ADR-026, epic 30, S30.12) folded in here instead of living
// on its own.

export const PRIVACY_ES: LegalDocumentWords = {
  title: "Política de privacidad",
  summary:
    "Qué datos personales trata {product}, por qué, durante cuánto tiempo, quién los recibe y los derechos que tienes conforme al RGPD. Una sección es para las personas cuyo perfil público ha leído {product}.",
  seoDescription: "Cómo {product} recopila, usa y protege tus datos, y los derechos que te da el RGPD.",
  sections: [
    {
      id: "controller",
      heading: "1. Quién es responsable",
      body: [
        "El responsable del tratamiento es {company}, {address}. Escribe a {contact} por cualquier asunto de esta política.",
        "Actuamos en dos roles. Para tu cuenta, los pagos, el sitio web y el funcionamiento de {product}, somos el responsable del tratamiento. Para las listas que un cliente construye y los correos que envía, tratamos datos en su nombre, conforme a la sección 8 de nuestros Términos del servicio.",
      ],
    },
    {
      id: "website",
      heading: "2. Visitar el sitio web",
      body: [
        "Nuestra red de distribución de contenido y nuestros servidores tratan brevemente tu dirección IP y los datos de tu navegador para entregar la página y mantenerla segura (art. 6.1.f RGPD). Nuestros registros de solicitudes guardan la página, la hora y el resultado, no tu dirección IP.",
        "Guardamos tu elección de cookies en tu navegador. No cargamos ninguna herramienta de publicidad.",
        "Si permites la analítica en Opciones de privacidad, el sitio web carga PostHog para contar visitas. Registra las páginas que abres y los botones que pulsas. También registra tu navegador, tu tipo de dispositivo, y el país y la ciudad que indica tu dirección IP. Todo ello se asocia a un identificador aleatorio que PostHog guarda en tu navegador. Nunca se registra lo que escribes. Los datos pasan por nuestra propia dirección y PostHog los guarda en la UE. Puedes retirar tu consentimiento cuando quieras en Opciones de privacidad (art. 6.1.a RGPD, § 25.1 TDDDG).",
        "Si permitiste la analítica y abres una cuenta desde el sitio web, guardamos ese identificador aleatorio con tu cuenta. Así sabemos qué visitas llevaron a un registro. Sin tu consentimiento no se vincula nada. Se borra con la cuenta (art. 6.1.a RGPD).",
        "Algunos enlaces llevan etiquetas de campaña, que nombran la campaña que los compartió. Mientras tu pestaña siga abierta, el sitio web guarda esas etiquetas, el sitio que te envió y la primera página que abriste. Las pasa al registro. Si creas una cuenta, las guardamos con ella una sola vez, para saber qué campaña la trajo (art. 6.1.f RGPD). Se borran con la cuenta.",
      ],
    },
    {
      id: "account",
      heading: "3. Tu cuenta",
      body: [
        "Para darte una cuenta, tratamos (art. 6.1.b RGPD):",
        {
          list: [
            "tu nombre, dirección de correo, contraseña (guardada solo como un hash fuerte), idioma, moneda y zona horaria, y una foto si añades una;",
            "si inicias sesión con Google o LinkedIn, el identificador y la dirección de correo que ese servicio nos envía;",
            "si añades una clave de acceso (passkey), su clave pública y una etiqueta;",
            "tus organizaciones, tu rol, invitaciones y miembros;",
            "tus sesiones: una cookie de sesión y la descripción de tu navegador, conservadas como máximo 30 días.",
          ],
        },
        "Para mantener {product} seguro, usamos tu dirección IP, acortada, para limitar intentos repetidos, y conservamos un registro de acciones importantes en tu organización durante 365 días (art. 6.1.f RGPD).",
        "Para dar una sola bienvenida por persona, guardamos tres datos junto a esa decisión (art. 6.1.f RGPD): un identificador aleatorio del navegador en una cookie de 400 días, tu bloque de red y un hash de tu correo. Tu bloque de red es tu dirección IP acortada, nunca la dirección completa.",
        "El formulario de registro usa Cloudflare Turnstile, que comprueba que lo rellena una persona y no un script. El anexo de privacidad de Turnstile de Cloudflare explica qué lee.",
        "Puedes borrar tu propia cuenta desde Configuración cuando quieras. Hacerlo inicia un período de gracia de 14 días antes de que se borre nada. Inicia sesión en cualquier momento dentro de esos 14 días y la cuenta se restaura, sin que se borre nada. Pasados los 14 días, el borrado se ejecuta y no se puede deshacer.",
        "Si le pides a nuestro personal que borre tu cuenta por ti, por ejemplo porque ya no puedes iniciar sesión, confirman tu dirección de correo primero. Luego registran un motivo y la borran de inmediato, sin período de gracia.",
      ],
    },
    {
      id: "payments",
      heading: "4. Pagos",
      body: [
        "Los pagos con tarjeta se procesan a través de Stripe y los pagos en bitcoin a través de Swiss Bitcoin Pay. Nunca vemos ni guardamos los datos de tu tarjeta.",
        "Conservamos tu nombre de facturación, dirección de correo, dirección postal, identificación fiscal, facturas, movimientos de créditos y, si nos lo dices, por qué cancelaste (art. 6.1.b y 6.1.c RGPD).",
        "Las facturas y los movimientos de crédito se conservan durante 10 años, el plazo que nos exige la ley fiscal y comercial alemana. Una vez que tu cuenta se borra, tu nombre, dirección e identificación fiscal se eliminan del registro; los montos, las fechas y los números de factura se mantienen.",
      ],
    },
    {
      id: "emails",
      heading: "5. Correos y notificaciones que te enviamos",
      body: [
        "Enviamos los correos que el servicio necesita, como enlaces de inicio de sesión, invitaciones, recibos y alertas (art. 6.1.b RGPD).",
        "Enviamos novedades del producto y consejos solo si los pediste (art. 6.1.a RGPD). Cada uno de esos correos tiene un enlace de baja de un clic.",
        "Si un correo dirigido a ti rebota o se marca como spam, mantenemos tu dirección en una lista que detiene nuevos envíos.",
        "Si permites las notificaciones push, el servicio push de tu navegador recibe una dirección de notificación de tu dispositivo. Puedes desactivarlas en tu navegador o en {product}.",
      ],
    },
    {
      id: "agents",
      heading: "6. Funciones de IA y agentes",
      body: [
        "Cuando un agente trabaja con nuestro modelo, la instrucción y el material que necesita se envían a Anthropic, que proporciona el modelo. No se usa para entrenar el modelo.",
        "Si inicias sesión con un agente en tu cuenta de Claude o Codex, o guardas en tu bóveda el acceso a un sitio web, esas credenciales quedan protegidas. Se cifran con claves guardadas en AWS Key Management Service. Se usan solo para las tareas que le asignas.",
      ],
    },
    {
      id: "team",
      heading: "7. Cómo se entera nuestro equipo de los eventos",
      body: [
        "Nuestro equipo se entera en un espacio de trabajo privado de Slack cuando alguien se registra (nombre y dirección de correo). También se entera cuando llega un pago y cuando un correo a un cliente falla. Esto mantiene el soporte receptivo (art. 6.1.f RGPD).",
        "Si te unes a la lista de espera o nos escribes, conservamos tu dirección de correo y tu mensaje para responderte.",
      ],
    },
    {
      id: "profiles",
      heading: "8. Si {product} ha leído tu perfil público",
      body: [
        "Esta sección es para las personas que nunca se registraron, cuyo perfil público de Instagram fue leído a solicitud de un cliente (art. 14 RGPD).",
        "Qué leemos: tu usuario, nombre, biografía, sitio web, número de seguidores y seguidos, si la cuenta es verificada o de empresa, su categoría y ciudad. También una dirección de correo o teléfono, solo si la hiciste pública en tu perfil. Podemos añadir campos derivados simples, como el idioma o el área de trabajo probable.",
        "De dónde procede: tu perfil público, a través de un proveedor de datos especializado. Nunca iniciamos sesión en tu cuenta ni leemos perfiles o mensajes privados.",
        "Por qué: nuestros clientes buscan contactos de empresas en su mercado. El cliente cuya búsqueda encontró tu perfil es el responsable del tratamiento de tus datos. Se basa en un interés legítimo en encontrar contactos en lo que decidiste hacer público (art. 6.1.f RGPD). Nosotros los tratamos en nombre de ese cliente.",
        "Quién lo recibe: solo el cliente cuya búsqueda encontró tu perfil. Ese cliente puede enviarte correos, guardarte en sus propios sistemas o mostrarte sus anuncios subiendo tus datos de contacto a una plataforma publicitaria. Debe informarte de ello, y dejar de hacerlo cuando te opongas.",
        "Durante cuánto tiempo: permanece en el espacio de trabajo de ese cliente hasta que lo elimina o cierra su cuenta.",
        "Tus derechos: puedes oponerte en cualquier momento, y pedir qué conservamos o que lo eliminemos. Escribe a {contact} con tu usuario. Eliminamos tu perfil de cada lista que conservamos. Un cliente que lo exportó antes debe responder por su propia copia: te decimos quién es si lo pides.",
      ],
    },
    {
      id: "recipients",
      heading: "9. Quién recibe los datos",
      body: [
        "No vendemos datos personales. Estos proveedores los tratan para nosotros, cada uno solo para su tarea:",
        {
          list: [
            "Amazon Web Services (Irlanda): servidores, base de datos, copias de seguridad, registros, claves de cifrado y el envío de nuestros correos;",
            "Cloudflare (Estados Unidos): distribución de contenido, protección del sitio web y la aplicación, y la comprobación del registro;",
            "PostHog (datos guardados en la UE): analítica del sitio web, solo si la permites;",
            "un proveedor especializado en datos públicos de Instagram: lectura de perfiles públicos;",
            "Stripe (Irlanda y Estados Unidos): pagos con tarjeta;",
            "Swiss Bitcoin Pay (Suiza): pagos en bitcoin;",
            "Resend (Estados Unidos): el envío de nuestros correos, junto a Amazon;",
            "Anthropic (Estados Unidos): el modelo detrás de los agentes;",
            "Slack (Estados Unidos): los avisos de nuestro equipo; un mensaje publicado no se puede recuperar;",
            "el servicio push de tu navegador, si permites las notificaciones push.",
          ],
        },
        "Google y LinkedIn reciben datos solo si decides iniciar sesión con ellos, conforme a sus propias políticas.",
        "Revelamos datos a las autoridades solo cuando la ley nos obliga a ello.",
      ],
    },
    {
      id: "transfers",
      heading: "10. Transferencias fuera de la UE",
      body: [
        "Cuando un proveedor trata datos fuera de la UE o del EEE, usamos una decisión de adecuación, como el EU-US Data Privacy Framework o el de Suiza. También podemos basarnos en las Cláusulas Contractuales Tipo de la UE.",
      ],
    },
    {
      id: "retention",
      heading: "11. Durante cuánto tiempo conservamos los datos",
      body: [
        {
          list: [
            "Datos de cuenta: mientras exista la cuenta, y luego un período de gracia de 14 días antes de eliminarla; iniciar sesión durante esos 14 días lo cancela. Un borrado que hace nuestro personal a tu solicitud, por ejemplo porque ya no puedes iniciar sesión, se ejecuta de inmediato, sin período de gracia.",
            "Sesiones: como máximo 30 días.",
            "Registro de actividad de la organización: 365 días.",
            "Un registro de las acciones de nuestro personal sobre tu cuenta: se conserva indefinidamente, para poder verificar la acción siempre; tu nombre se elimina de él.",
            "Tu dirección en nuestra lista de exclusión de correos se conserva indefinidamente, incluso después de que todo lo demás se borre. Esto ocurre si un mensaje a ti rebotó o lo marcaste como no deseado.",
            "Registros del servidor: 30 días. Copias de seguridad de la base de datos: 7 días.",
            "Facturas y movimientos de crédito: 10 años.",
            "Perfiles en las listas de un cliente: hasta que el cliente los elimina o cierra su cuenta.",
          ],
        },
      ],
    },
    {
      id: "security",
      heading: "12. Seguridad",
      body: [
        "Los datos viajan cifrados. La base de datos está cifrada y no es accesible desde internet. Las contraseñas se guardan solo como hashes fuertes, y los secretos en la bóveda se cifran con claves gestionadas.",
        "Solo el personal que lo necesita puede acceder a los datos de los clientes. Su acceso se otorga por persona, se registra y se vigila con alarmas.",
      ],
    },
    {
      id: "rights",
      heading: "13. Tus derechos",
      body: [
        "Puedes pedir el acceso a tus datos, su corrección o eliminación, una limitación de su uso, o una copia para llevarte a otra parte. Puedes oponerte al tratamiento basado en interés legítimo, y retirar un consentimiento en cualquier momento sin afectar lo ya ocurrido.",
        "Escribe a {contact}. Respondemos dentro de un mes.",
        "También puedes reclamar ante una autoridad de control. La nuestra es la {authority}.",
        "No tomamos ninguna decisión sobre ti con efectos legales o igualmente significativos basada solo en medios automatizados. Las puntuaciones de leads ayudan a nuestros clientes a ordenar sus propias listas.",
      ],
    },
    {
      id: "changes",
      heading: "14. Cambios",
      body: [
        "Actualizamos esta política cuando cambia lo que tratamos. La fecha en la parte superior muestra la versión vigente: avisamos por correo a los titulares de cuenta sobre cambios importantes.",
      ],
    },
  ],
};
